import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    MenuItem,
    Select,
    FormControl,
    Grid,
    IconButton,
    CircularProgress,
    CardMedia,
    FormControlLabel,
    Switch,
    Alert,
    Chip,
    Backdrop
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useDropzone } from 'react-dropzone';
import * as pdfjsLib from "pdfjs-dist";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// --- STYLING MACROS ---
const glassPanelStyle = (isProcessing) => ({
    background: isProcessing ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid',
    // Switch to a solid, distinct color signature instead of shifting layout vectors
    borderColor: isProcessing ? 'secondary.light' : 'rgba(0, 0, 0, 0.06)',
    borderRadius: '16px',
    boxShadow: isProcessing ? '0 4px 20px 0 rgba(156, 39, 176, 0.15)' : '0 4px 20px 0 rgba(0, 0, 0, 0.03)',
    p: { xs: 1.5, sm: 2.5 },
    mb: 2.5,
    position: 'relative',
    transition: 'background-color 0.3s ease, border-color 0.3s ease' // Smooth, uniform canvas transitions
});
const subSectionBoxStyle = {
    background: 'rgba(0, 0, 0, 0.02)',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    borderRadius: '12px',
    p: 1.5,
    mb: 1.5
};
const glassInputStyle = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '8px',
        transition: 'all 0.2s ease-in-out',
        '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.3)' },
        '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: '1.5px' }
    }
};
const glassSelectStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.15)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.3)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: '1.5px' }
};
const glassSecondaryButtonStyle = {
    background: 'rgba(0, 0, 0, 0.03)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '10px',
    color: 'text.primary',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.06)',
        borderColor: 'rgba(0, 0, 0, 0.15)',
        transform: 'translateY(-1px)'
    },
    '&:active': {
        transform: 'translateY(0)'
    }
};
const CATEGORY_LABELS = {
    lodging: "Lodging",
    meals: "Meals",
    activities: "Activities",
    flights: "Flights",
    supplies: "Supplies",
    incidentals: "Incidentals",
    transportation: "Transportation",
    shopping: "Shopping",
    general: "General"
};
const isDesktop = () => {
    if (typeof navigator === 'undefined') return false;
    return !/Android|iPhone|iPad|iPod|ChromeOS/i.test(navigator.userAgent);
};
const extractTextFromPDF = async (data) => {
    console.log("[ReceiptScanner] Initializing PDFJS Text Extraction pipeline...");
    const loadingTask = pdfjsLib.getDocument({ data });
    const pdf = await loadingTask.promise;
    console.log(`[ReceiptScanner] PDF successfully resolved. Total page count: ${pdf.numPages}`);
    let fullText = "";
    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(" ");
        console.log(`[ReceiptScanner] Extracted ${textContent.items.length} text items from Page ${i}`);
        fullText += pageText + "\n";
    }
    return fullText;
};
const formatMMSS = (ms) => {
    if (!ms || ms < 0) return '00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};



export default function ReceiptScanner({ open, onClose, onSaveReceipt }) {

    const [scannedFiles, setScannedFiles] = useState([]);
    const [useExperimentalAi, setUseExperimentalAi] = useState(true);
    const [currentTime, setCurrentTime] = useState(Date.now());

    const [zoomImageSrc, setZoomImageSrc] = useState(null);
    const [isImageZoomedIn, setIsImageZoomedIn] = useState(false);

    const sessionRef = useRef(null);
    const [availabilityStatus, setAvailabilityStatus] = useState('checking');
    const [loadingStatus, setLoadingStatus] = useState('Checking API status...');
    const [isDesktopDevice, setIsDesktopDevice] = useState(false);

    useEffect(() => {
        let ticker = null;

        // Check processing array values contextually
        const hasActiveProcessingJobs = scannedFiles.some(f => f.isProcessing);

        if (hasActiveProcessingJobs) {
            ticker = setInterval(() => {
                // Update time context linearly without forcing absolute layout rewrites
                setCurrentTime(Date.now());
            }, 1000); // Shift to a standard 1-second interval cadence to minimize rendering fatigue
        }

        return () => {
            if (ticker) clearInterval(ticker);
        };
        // ONLY listen to changes in execution states, NOT individual file tracking arrays
    }, [scannedFiles.map(f => f.isProcessing).join(',')]);

    useEffect(() => {
        if (!open) return;

        console.log("[ReceiptScanner] Environment assessment running...");
        if (!isDesktop()) {
            console.warn("[ReceiptScanner] Target environment is mobile/tablet. Local LanguageModel blocked.");
            setIsDesktopDevice(false);
            setLoadingStatus('This API only works on desktop devices.');
            setAvailabilityStatus('unavailable');
            return;
        }
        setIsDesktopDevice(true);

        const verifyLanguageModelEngine = async () => {
            if (typeof window === 'undefined') return;
            console.log("[ReceiptScanner] Testing window.LanguageModel architecture availability...");
            if (!('LanguageModel' in window)) {
                console.error("[ReceiptScanner] LanguageModel API is absent from window context.");
                setAvailabilityStatus('unavailable');
                setLoadingStatus('LanguageModel API is not supported in this environment.');
                return;
            }

            try {
                const status = await window.LanguageModel.availability({
                    expectedInputs: [{ type: 'text', languages: ['en'] }],
                    expectedOutputs: [{ type: 'text', languages: ['en'] }],
                });
                console.log("[ReceiptScanner] LanguageModel availability status resolved:", status);

                if (status === 'downloading') {
                    setAvailabilityStatus('downloading');
                    setLoadingStatus('Model is downloading...');
                    return;
                }

                if (status !== 'available' && status !== 'downloadable') {
                    console.error("[ReceiptScanner] Hardware tokenization or offline assets unavailable.");
                    setAvailabilityStatus('unavailable');
                    setLoadingStatus('No localized language model assets found on device.');
                    return;
                }

                if (!sessionRef.current && useExperimentalAi) {
                    console.log("[ReceiptScanner] Spawning new LanguageModel orchestrator session...");
                    sessionRef.current = await window.LanguageModel.create({
                        expectedInputs: [
                            { type: 'image', languages: ['en'] },
                            { type: 'text', languages: ['en'] }
                        ],
                        expectedOutputs: [{ type: 'text', languages: ['en'] }]
                    });
                    console.log("[ReceiptScanner] LanguageModel orchestrator session loaded successfully.");
                }
                setAvailabilityStatus('available');
                setLoadingStatus('APIs ready to use!');
            } catch (error) {
                console.error("[ReceiptScanner] Failed setting up LanguageModel engine session:", error);
                setAvailabilityStatus('unavailable');
                setLoadingStatus(`API error: ${error.message}`);
            }
        };

        verifyLanguageModelEngine();
    }, [open, useExperimentalAi]);

    const processReceiptWithAI = async (fileId, dataToUse) => {
        console.log(`[ReceiptScanner] Requesting AI inference for execution ID: ${fileId}`);
        const activeSession = sessionRef.current;
        if (!useExperimentalAi || !dataToUse || !activeSession) {
            console.warn("[ReceiptScanner] Prompt skipped. AI flag false, missing payload, or session uninitialized.");
            return;
        }

        try {
            let contentPart;
            if (typeof dataToUse === 'string' && !dataToUse.startsWith('data:')) {
                console.log(`[ReceiptScanner] Processing context as plain text string. Length: ${dataToUse.length}`);
                const cleanText = dataToUse.replace(/Received:[\s\S]*?\r?\n(?!\s)/g, '').replace(/Authentication-Results:[\s\S]*?\r?\n(?!\s)/g, '').trim();
                const MAX_CHARS = 15000;
                contentPart = { type: 'text', value: cleanText.length > MAX_CHARS ? `${cleanText.substring(0, MAX_CHARS)}\n[...]` : cleanText };
            } else if (typeof dataToUse === 'string' && dataToUse.startsWith('data:')) {
                console.log("[ReceiptScanner] Processing context as Base64 Data URI string format.");
                const response = await fetch(dataToUse);
                const blob = await response.blob();
                if (blob.type === 'application/pdf') {
                    console.log("[ReceiptScanner] Base64 matched PDF mime-type. Directing text extraction fallback.");
                    contentPart = { type: 'text', value: `PDF FALLBACK: ${dataToUse.substring(0, 5000)}` };
                } else {
                    console.log(`[ReceiptScanner] Image blob generated. Type: ${blob.type}, Size: ${blob.size} bytes`);
                    contentPart = { type: 'image', value: blob };
                }
            }

            console.log("[ReceiptScanner] Dispatching prompt to device execution model...");
            const apiResponse = await activeSession.prompt([
                {
                    role: 'user',
                    content: [
                        {
                            type: 'text',
                            value: `You are a specialized expense parser. Analyze this document and return a JSON object. 
                                DO NOT wrap the object in a parent key like "receipt". Return a flat dictionary structure only.
                                
                                ### STRICT CATEGORIES (Must be exactly one of these lowercase values):
                                lodging, meals, activities, flights, transportation, supplies, incidentals, shopping, general
                                
                                ### AUTOMATED CATEGORIZATION LOGIC:
                                Analyze merchant names, line items, and contextual phrases to deduce the correct category:
                                1. 'transportation': Trigger for fuel, gas station brands (like Loves, Pilot, Shell), "pump", "gal", "gallons", "diesel", "unleaded", tolls, parking, or rideshares.
                                2. 'meals': Trigger for restaurants, cafes, fast food, coffee shops, bars, or explicit prepared food services.
                                3. 'supplies': Trigger for raw grocery items, generic store stock, or miscellaneous "food items" bought at a convenience/grocery store rather than a restaurant.
                                4. 'lodging': Trigger for hotels, motels, room rates, stays, "nights", or accommodation fees.
                                
                                ### EXTRACTION RULES:
                                1. **Dates**: 
                                   - "receiptDate": The transaction/payment date (YYYY-MM-DD).
                                   - For **lodging**: Find "Check-in" and "Check-out" dates. Add keys "startDate" and "endDate".
                                   - For **activities**: Find "Date of Service". Add key "serviceDate".
                                2. **Financials**:
                                   - "grandTotal": Final amount. RETURN AS A NUMERIC STRING ONLY (e.g., "1354.80"). Remove all symbols like $ or commas.
                                   - "currency": Identify the 3-letter currency code (e.g., "USD", "EUR", "GBP").
                                   - "amountDue": Set to "0.00" if paid in full or if change is 0.
                                3. **Location Breakdown**:
                                   - Split the location into "businessAddress" (Street), "businessCity", "businessState", "businessZip", and "businessCountry".
                                
                                ### OUTPUT FORMAT:
                                Return ONLY a valid JSON object. Use "Not found" for missing fields. 
                                Include exactly these keys: 
                                "category", "currency", "businessName", "businessAddress", "businessCity", "businessState", "businessZip", 
                                "businessCountry", "businessPhone", "businessEmail", "businessWebsite", "receiptID", "receiptDate", "grandTotal", "amountDue", 
                                "bookedThrough", "confirmationCode", "reservationName", "reservationNumber", "notes".
                                
                                IMPORTANT: Do not hallucinate. If you don't see a field, return "Not found".`
                        },
                        contentPart
                    ],
                },
            ]);

            console.log("[ReceiptScanner] Raw generation payload received back from model framework.", apiResponse);
            if (!apiResponse) throw new Error("AI_EMPTY_RESPONSE");

            const cleaned = apiResponse.replace(/```json/g, "").replace(/```/g, "").trim();
            console.log("[ReceiptScanner] Cleaned response output string:", cleaned);
            const parsedResult = JSON.parse(cleaned);

            if (parsedResult) {
                console.log("[ReceiptScanner] Schema parsed correctly. Cleaning string exceptions and merging fields...");

                // Fallback look-ahead if the AI nested it under a "receipt" key anyway
                const rawData = parsedResult.receipt ? parsedResult.receipt : parsedResult;

                // Direct helper to sweep out "Not found" string flags to prevent cluttering text input fields
                const sanitize = (val) => {
                    if (!val) return '';
                    const cleanVal = String(val).trim();
                    return cleanVal.toLowerCase() === 'not found' ? '' : cleanVal;
                };

                // --- NEW: ROBUST DATE NORMALIZER ---
                const normalizeDate = (dateStr) => {
                    const cleanDate = sanitize(dateStr);
                    if (!cleanDate) return '';

                    // Check if date is in MM/DD/YY or MM/DD/YYYY format
                    const match = cleanDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
                    if (match) {
                        let [_, month, day, year] = match;
                        month = month.padStart(2, '0');
                        day = day.padStart(2, '0');

                        // Handle 2-digit year vs 4-digit year conversion safely
                        if (year.length === 2) {
                            year = `20${year}`; // Assumes 21st century
                        }
                        return `${year}-${month}-${day}`; // Outputs pristine YYYY-MM-DD
                    }
                    return cleanDate; // Fallback to raw string if it's already YYYY-MM-DD
                };

                // Clean data mappings matching your successful prompt architecture
                const mappedFormFields = {
                    businessName: sanitize(rawData.businessName || rawData.store_name),
                    receiptID: sanitize(rawData.receiptID || rawData.ticket_number || rawData.invoice_number),
                    receiptDate: normalizeDate(rawData.receiptDate || rawData.date),
                    grandTotal: rawData.grandTotal ? String(rawData.grandTotal).replace(/[$,\s]/g, "") : (rawData.total_sale ? String(rawData.total_sale).replace(/[$,\s]/g, "") : ''),
                    currency: sanitize(rawData.currency) || 'USD',
                    amountDue: sanitize(rawData.amountDue) || '0.00',
                    businessPhone: sanitize(rawData.businessPhone),
                    businessEmail: sanitize(rawData.businessEmail),
                    businessWebsite: sanitize(rawData.businessWebsite),
                    businessAddress: sanitize(rawData.businessAddress),
                    businessCity: sanitize(rawData.businessCity),
                    businessState: sanitize(rawData.businessState),
                    businessZip: sanitize(rawData.businessZip),
                    businessCountry: sanitize(rawData.businessCountry),
                    bookedThrough: sanitize(rawData.bookedThrough),
                    confirmationCode: sanitize(rawData.confirmationCode),
                    reservationName: sanitize(rawData.reservationName),
                    reservationNumber: sanitize(rawData.reservationNumber),
                    startDate: normalizeDate(rawData.startDate),
                    endDate: normalizeDate(rawData.endDate),
                    serviceDate: normalizeDate(rawData.serviceDate),
                    notes: sanitize(rawData.notes)
                };

                const fallbackCategory = Object.keys(CATEGORY_LABELS).includes(rawData.category?.toLowerCase())
                    ? rawData.category.toLowerCase()
                    : 'general';

                console.log("[ReceiptScanner] Sanitized fields constructed successfully:", mappedFormFields);

                setScannedFiles(prev => prev.map(item => {
                    if (item.id === fileId) {
                        return {
                            ...item,
                            isProcessing: false,
                            finalDurationMs: Date.now() - item.startTime,
                            editableForm: {
                                ...item.editableForm,
                                ...mappedFormFields,
                                category: fallbackCategory
                            }
                        };
                    }
                    return item;
                }));
            }
        } catch (e) {
            console.error(`[ReceiptScanner] Processing error encountered during job execution ${fileId}:`, e);
            setScannedFiles(prev => prev.map(item => {
                if (item.id === fileId) {
                    return { ...item, isProcessing: false, finalDurationMs: Date.now() - item.startTime, aiError: "⚠️ Automated indexing failed. " + e.message };
                }
                return item;
            }));
        }
    };

    const onDrop = useCallback((acceptedFiles) => {
        console.log(`[ReceiptScanner] File Drop hook fired. Items count: ${acceptedFiles.length}`);
        acceptedFiles.forEach(async (file) => {
            console.log(`[ReceiptScanner] Checking file configuration: Name="${file.name}", Type="${file.type}", Size=${file.size}`);
            const fileName = file.name.toLowerCase();
            const isEml = fileName.endsWith(".eml");
            const isPdf = file.type === "application/pdf";

            const validTypes = ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf", "message/rfc822", "application/octet-stream"];
            if (!validTypes.includes(file.type) && !isEml) {
                console.warn(`[ReceiptScanner] File type rejection applied on file boundary: ${file.type}`);
                return;
            }

            const fileId = `${Date.now()}-${Math.random()}`;
            const determinedType = isEml ? "message/rfc822" : file.type;

            const initialFormState = {
                category: '', grandTotal: '', currency: '', receiptDate: '', businessName: '', businessAddress: '', businessCity: '',
                businessState: '', businessZip: '', businessCountry: '', businessPhone: '', businessEmail: '', businessWebsite: '',
                receiptID: '', bookedThrough: '', confirmationCode: '', reservationName: '', reservationNumber: '', notes: '',
                startDate: '', endDate: '', serviceDate: '', amountDue: ''
            };

            setScannedFiles(prev => [...prev, {
                id: fileId, isProcessing: true, startTime: Date.now(), finalDurationMs: null, aiError: null, previewData: null, isExpanded: true,
                receiptFile: { name: file.name, type: determinedType, size: file.size, full_data: null }, editableForm: initialFormState
            }]);

            const reader = new FileReader();
            reader.onload = async () => {
                console.log(`[ReceiptScanner] FileReader payload generation successful for ID: ${fileId}`);
                const fileContent = reader.result;
                let dataForAi = fileContent;

                if (isPdf) {
                    try {
                        console.log("[ReceiptScanner] Target document matches PDF. Dispatching buffer array conversions...");
                        const response = await fetch(fileContent);
                        const arrayBuffer = await response.arrayBuffer();
                        dataForAi = `EXTRACTED PDF TEXT:\n${await extractTextFromPDF(arrayBuffer)}`;
                    } catch (e) {
                        console.error("[ReceiptScanner] High level error occurred converting PDF array bounds:", e);
                        dataForAi = fileContent;
                    }
                }

                setScannedFiles(prev => prev.map(item => {
                    if (item.id === fileId) return { ...item, previewData: dataForAi, receiptFile: { ...item.receiptFile, full_data: fileContent } };
                    return item;
                }));
                processReceiptWithAI(fileId, dataForAi);
            };

            if (isEml) {
                console.log("[ReceiptScanner] Processing EML structure stream data...");
                reader.readAsText(file);
            } else {
                reader.readAsDataURL(file);
            }
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [], "application/pdf": [], "message/rfc822": [".eml"] }
    });

    const handleToggleExpand = (id) => {
        setScannedFiles(prev => prev.map(item => {
            if (item.id === id) return { ...item, isExpanded: !item.itemHasCustomToggle ? !item.isExpanded : item.isExpanded };
            // Alternate clean direct map:
            return item.id === id ? { ...item, isExpanded: !item.isExpanded } : item;
        }));
    };

    const handleRemoveFile = (id, e) => {
        if (e) e.stopPropagation();
        console.log(`[ReceiptScanner] Purging record entry tracking matrix item ref: ${id}`);
        setScannedFiles(prev => prev.filter(item => item.id !== id));
    };

    const handleUpdateFormField = (id, field, value) => {
        setScannedFiles(prev => prev.map(item => {
            if (item.id === id) return { ...item, editableForm: { ...item.editableForm, [field]: value } };
            return item;
        }));
    };

    const handleSaveSingleItem = (id) => {
        console.log(`[ReceiptScanner] Initiating isolation validation save routine on item ID: ${id}`);
        const singleItem = scannedFiles.find(item => item.id === id);
        if (singleItem) {
            console.log("[ReceiptScanner] Relaying payload back to dynamic client application logic:", singleItem.editableForm);
            onSaveReceipt(singleItem.editableForm);
            setScannedFiles(prev => prev.filter(item => item.id !== id));
        } else {
            console.error(`[ReceiptScanner] Core matching integrity lookup fault on saving operation target execution: ${id}`);
        }
    };

    const handleClearAll = () => setScannedFiles([]);

    const renderStatusBadge = () => {
        if (!isDesktopDevice && availabilityStatus === 'unavailable') {
            return <Chip size="small" icon={<ErrorOutlineIcon />} label="Desktop Required" color="error" variant="outlined" sx={{ borderRadius: '6px' }} />;
        }
        switch (availabilityStatus) {
            case 'checking': return <Chip size="small" icon={<CircularProgress size={12} />} label="Checking Engine..." color="default" sx={{ borderRadius: '6px' }} />;
            case 'downloading': return <Chip size="small" icon={<DownloadForOfflineIcon />} label="Syncing..." color="warning" sx={{ borderRadius: '6px' }} />;
            case 'available':
                const processingCount = scannedFiles.filter(f => f.isProcessing).length;
                return <Chip size="small" icon={processingCount > 0 ? <CircularProgress size={12} color="inherit" /> : <CheckCircleOutlineIcon />} label={processingCount > 0 ? `Parsing (${processingCount})...` : "Engine Ready"} color={processingCount > 0 ? "secondary" : "success"} sx={{ borderRadius: '8px', fontWeight: 'bold' }} />;
            default: return <Chip size="small" icon={<ErrorOutlineIcon />} label="AI Unavailable" color="error" variant="outlined" sx={{ borderRadius: '6px' }} />;
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
                    PaperProps={{ style: { background: 'rgba(248, 249, 250, 0.95)', backdropFilter: 'blur(20px)', borderRadius: '16px' } }}
            >
                {/* HEADER ROW WITH COMPACT DROPZONE INTEGRATED */}
                <DialogTitle sx={{ p: { xs: 1.5, sm: 2.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} sm={4}>
                            {/* Top Row: Title & Status Badge */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography variant="h6" fontWeight="800" sx={{ color: '#1a252f', letterSpacing: '-0.5px' }}>
                                    Receipt Portal
                                </Typography>
                                {renderStatusBadge()}
                            </Box>
                            {/* Bottom Row: Inline Caption directly underneath */}
                            <Typography
                                variant="caption"
                                fontWeight="600"
                                sx={{
                                    color: 'error.main',
                                    display: 'block',
                                    lineHeight: 1.2,
                                    mt: 0.5
                                }}
                            >
                                ⚠️ Double-check all inputs: AI can be fucked up and remarkably stupid.
                            </Typography>
                        </Grid>

                        {/* Integrated Header Dropzone Button */}
                        <Grid item xs={12} sm={5} md={4.5}>
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: isDragActive ? '2px dashed primary.main' : '1px dashed rgba(0,0,0,0.2)',
                                    borderRadius: '8px',
                                    px: 2,
                                    py: 1,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: isDragActive ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0,0,0,0.02)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    transition: 'all 0.2s',
                                    '&:hover': { borderColor: 'primary.main', background: '#fff' }
                                }}
                            >
                                <input {...getInputProps()} />
                                <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="body2" fontWeight="700" sx={{ whiteSpace: 'nowrap' }}>
                                    {isDragActive ? 'Drop receipts here!' : 'Click or Drag to Upload'}
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={3} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                            <FormControlLabel
                                control={<Switch checked={useExperimentalAi} disabled={availabilityStatus === 'unavailable'} onChange={(e) => setUseExperimentalAi(e.target.checked)} size="small" />}
                                label={<Typography variant="caption" fontWeight="bold" color="text.secondary">AI Auto-Parse</Typography>}
                            />
                        </Grid>
                    </Grid>
                </DialogTitle>

                <DialogContent sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: 'rgba(255,255,255,0.4)' }}>
                    {availabilityStatus !== 'available' && (
                        <Alert severity={availabilityStatus === 'downloading' ? 'warning' : 'error'} sx={{ mb: 2, borderRadius: '8px' }}>
                            {loadingStatus}
                        </Alert>
                    )}

                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            {scannedFiles.length === 0 ? (
                                <Box sx={{ ...glassPanelStyle(false), textAlign: 'center', py: 10, borderStyle: 'dashed', mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight="600">
                                        No transaction logs loaded. Use the top upload button to populate your verification workspace.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: { xs: 'none', md: '72vh' }, overflowY: 'auto' }}>
                                    {scannedFiles.map((item) => {
                                        const msElapsed = item.isProcessing ? (currentTime - item.startTime) : item.finalDurationMs;
                                        const displayClockString = formatMMSS(msElapsed);
                                        const isExpanded = item.isExpanded !== false;

                                        return (
                                            <Box key={item.id} sx={glassPanelStyle(item.isProcessing)}>
                                                {/* CARD TOP CONTROL BAR */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, pb: 1, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '60%' }}>
                                                        <IconButton
                                                            onClick={() => handleToggleExpand(item.id)}
                                                            size="small"
                                                            sx={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }}
                                                        >
                                                            <ExpandMoreIcon fontSize="small" />
                                                        </IconButton>
                                                        <AutoAwesomeIcon sx={{ color: item.isProcessing ? 'secondary.main' : 'primary.main', fontSize: 15 }} />
                                                        <Typography variant="body2" fontWeight="700" noWrap>{item.receiptFile.name}</Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Chip size="small" icon={<AccessTimeIcon style={{ fontSize: '11px' }} />} label={displayClockString} color={item.isProcessing ? "secondary" : "default"} variant="outlined" sx={{ height: '18px', fontSize: '10px', fontWeight: 'bold' }} />
                                                        <IconButton onClick={(e) => handleRemoveFile(item.id, e)} size="small" sx={{ p: 0.5, '&:hover': { color: 'error.main' } }}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Box>
                                                </Box>

                                                {item.aiError && <Alert severity="warning" sx={{ mb: 1.5, py: 0, px: 1.5, fontSize: '11px', borderRadius: '6px' }}>{item.aiError}</Alert>}

                                                {item.isProcessing ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '140px', gap: 1, py: 3 }}>
                                                        <CircularProgress size={28} color="secondary" />
                                                        <Typography variant="caption" fontWeight="700" color="secondary.main">Locally Indexing Metadata Structure...</Typography>
                                                    </Box>
                                                ) : (
                                                    <Box>
                                                        {/* SECTION 1: CORE FINANCIAL DETAILS + EMBEDDED THUMBNAIL (ADAPTIVE VIEW) */}
                                                        <Box sx={subSectionBoxStyle}>
                                                            <Grid container spacing={1.5} alignItems="center">
                                                                <Grid item xs={3} sm={2} md={1.5}>
                                                                    <Box
                                                                        onClick={() => item.receiptFile.type?.startsWith('image/') && item.receiptFile.full_data && setZoomImageSrc(item.receiptFile.full_data)}
                                                                        sx={{
                                                                            width: '100%',
                                                                            height: isExpanded ? { xs: '100px', sm: '115px' } : '50px', // Shrinks cleanly when collapsed
                                                                            background: 'rgba(0,0,0,0.05)',
                                                                            borderRadius: '8px',
                                                                            overflow: 'hidden',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            border: '1px solid rgba(0,0,0,0.08)',
                                                                            position: 'relative',
                                                                            cursor: item.receiptFile.type?.startsWith('image/') ? 'zoom-in' : 'default',
                                                                            transition: 'height 0.2s ease'
                                                                        }}
                                                                    >
                                                                        {item.receiptFile.type?.startsWith('image/') && item.receiptFile.full_data ? (
                                                                            <CardMedia component="img" image={item.receiptFile.full_data} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                                        ) : (
                                                                            <Typography variant="caption" fontWeight="800" color="text.secondary">{item.receiptFile.name.split('.').pop()?.toUpperCase() || 'LOG'}</Typography>
                                                                        )}
                                                                    </Box>
                                                                </Grid>

                                                                <Grid item xs={9} sm={10} md={10.5}>
                                                                    {isExpanded ? (
                                                                        /* FULL EXPANDED CORE INPUTS */
                                                                        <Grid container spacing={1.5}>
                                                                            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Merchant Name" sx={glassInputStyle} value={item.editableForm.businessName} onChange={(e) => handleUpdateFormField(item.id, 'businessName', e.target.value)} /></Grid>
                                                                            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Grand Total" sx={glassInputStyle} value={item.editableForm.grandTotal} onChange={(e) => handleUpdateFormField(item.id, 'grandTotal', e.target.value)} /></Grid>
                                                                            <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Currency" sx={glassInputStyle} value={item.editableForm.currency} onChange={(e) => handleUpdateFormField(item.id, 'currency', e.target.value)} /></Grid>
                                                                            <Grid item xs={6} sm={2}>
                                                                                <FormControl fullWidth size="small" sx={glassSelectStyle}>
                                                                                    <Select value={item.editableForm.category} onChange={(e) => handleUpdateFormField(item.id, 'category', e.target.value)}>
                                                                                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
                                                                                    </Select>
                                                                                </FormControl>
                                                                            </Grid>
                                                                        </Grid>
                                                                    ) : (
                                                                        /* COMPACT COLLAPSED DASHBOARD LABELS */
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                                                                            <Box>
                                                                                <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                                                                                    {item.editableForm.businessName || "Unknown Merchant"}
                                                                                </Typography>
                                                                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                                                                                    Category: {item.editableForm.category || 'general'}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Box sx={{ textAlign: 'right' }}>
                                                                                <Typography variant="h6" fontWeight="900" color="primary.main" sx={{ letterSpacing: '-0.5px' }}>
                                                                                    {item.editableForm.grandTotal ? parseFloat(item.editableForm.grandTotal).toFixed(2) : '0.00'} <Typography component="span" variant="caption" fontWeight="700">{item.editableForm.currency || 'USD'}</Typography>
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    )}
                                                                </Grid>
                                                            </Grid>
                                                        </Box>

                                                        {/* EXTENDED SECTIONS: RENDER CONDITIONAL ON EXPANSION STATE */}
                                                        {isExpanded && (
                                                            <>
                                                                {/* SECTION 2: MERCHANT DETAILS */}
                                                                <Box sx={subSectionBoxStyle}>
                                                                    <Typography variant="caption" fontWeight="800" color="primary.main" display="block" sx={{ mb: 1.5, textTransform: 'uppercase' }}>2. Extended Metadata Logs</Typography>
                                                                    <Grid container spacing={1.5}>
                                                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Invoice/Receipt ID" sx={glassInputStyle} value={item.editableForm.receiptID} onChange={(e) => handleUpdateFormField(item.id, 'receiptID', e.target.value)} /></Grid>
                                                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" type="date" label="Transaction Date" sx={glassInputStyle} InputLabelProps={{ shrink: true }} value={item.editableForm.receiptDate} onChange={(e) => handleUpdateFormField(item.id, 'receiptDate', e.target.value)} /></Grid>
                                                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Amount Due" sx={glassInputStyle} value={item.editableForm.amountDue} onChange={(e) => handleUpdateFormField(item.id, 'amountDue', e.target.value)} /></Grid>
                                                                    </Grid>
                                                                </Box>

                                                                {/* SECTION 3: GEOGRAPHIC BREAKDOWN */}
                                                                <Box sx={subSectionBoxStyle}>
                                                                    <Typography variant="caption" fontWeight="800" color="primary.main" display="block" sx={{ mb: 1.5, textTransform: 'uppercase' }}>3. Location Geography</Typography>
                                                                    <Grid container spacing={1.5}>
                                                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Street Address" sx={glassInputStyle} value={item.editableForm.businessAddress} onChange={(e) => handleUpdateFormField(item.id, 'businessAddress', e.target.value)} /></Grid>
                                                                        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="City" sx={glassInputStyle} value={item.editableForm.businessCity} onChange={(e) => handleUpdateFormField(item.id, 'businessCity', e.target.value)} /></Grid>
                                                                        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="State/Region" sx={glassInputStyle} value={item.editableForm.businessState} onChange={(e) => handleUpdateFormField(item.id, 'businessState', e.target.value)} /></Grid>
                                                                        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Zip/Postal" sx={glassInputStyle} value={item.editableForm.businessZip} onChange={(e) => handleUpdateFormField(item.id, 'businessZip', e.target.value)} /></Grid>
                                                                        <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Country" sx={glassInputStyle} value={item.editableForm.businessCountry} onChange={(e) => handleUpdateFormField(item.id, 'businessCountry', e.target.value)} /></Grid>
                                                                    </Grid>
                                                                </Box>

                                                                {/* SECTION 4: BOOKING PROPERTIES */}
                                                                <Box sx={subSectionBoxStyle}>
                                                                    <Typography variant="caption" fontWeight="800" color="primary.main" display="block" sx={{ mb: 1.5, textTransform: 'uppercase' }}>4. Extended Itinerary Parameters</Typography>
                                                                    <Grid container spacing={1.5}>
                                                                        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Booked Through" sx={glassInputStyle} value={item.editableForm.bookedThrough} onChange={(e) => handleUpdateFormField(item.id, 'bookedThrough', e.target.value)} /></Grid>
                                                                        <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Confirmation #" sx={glassInputStyle} value={item.editableForm.confirmationCode} onChange={(e) => handleUpdateFormField(item.id, 'confirmationCode', e.target.value)} /></Grid>
                                                                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" type="date" label="Start Date" sx={glassInputStyle} InputLabelProps={{ shrink: true }} value={item.editableForm.startDate} onChange={(e) => handleUpdateFormField(item.id, 'startDate', e.target.value)} /></Grid>
                                                                        <Grid item xs={12} sm={3}><TextField fullWidth size="small" type="date" label="End Date" sx={glassInputStyle} InputLabelProps={{ shrink: true }} value={item.editableForm.endDate} onChange={(e) => handleUpdateFormField(item.id, 'endDate', e.target.value)} /></Grid>
                                                                        <Grid item xs={12}>
                                                                            <TextField fullWidth size="small" multiline rows={2} label="Expense Log Notes" sx={glassInputStyle} value={item.editableForm.notes} onChange={(e) => handleUpdateFormField(item.id, 'notes', e.target.value)} />
                                                                        </Grid>
                                                                    </Grid>
                                                                </Box>

                                                                {/* ISOLATED ACTIONS FOOTER WITH REFINED GLASS BUTTON */}
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, pt: 1, borderTop: '1px dashed rgba(0,0,0,0.06)' }}>
                                                                    <Button
                                                                        variant="contained"
                                                                        startIcon={<SaveIcon />}
                                                                        onClick={() => handleSaveSingleItem(item.id)}
                                                                        sx={{
                                                                            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.15) 0%, rgba(25, 118, 210, 0.08) 100%)',
                                                                            backdropFilter: 'blur(8px)',
                                                                            WebkitBackdropFilter: 'blur(8px)',
                                                                            border: '1px solid rgba(25, 118, 210, 0.25)',
                                                                            borderRadius: '10px',
                                                                            color: 'primary.main',
                                                                            fontWeight: '700',
                                                                            textTransform: 'none',
                                                                            letterSpacing: '0.4px',
                                                                            boxShadow: 'none',
                                                                            px: 3,
                                                                            py: 0.8,
                                                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                            '&:hover': {
                                                                                background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.22) 0%, rgba(25, 118, 210, 0.12) 100%)',
                                                                                borderColor: 'primary.main',
                                                                                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.18)',
                                                                                transform: 'translateY(-1.5px)'
                                                                            },
                                                                            '&:active': {
                                                                                transform: 'translateY(0)',
                                                                                background: 'rgba(25, 118, 210, 0.25)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        Save This Item
                                                                    </Button>
                                                                </Box>
                                                            </>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, px: { xs: 2, sm: 3 }, borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        onClick={onClose}
                        sx={glassSecondaryButtonStyle}
                    >
                        Close Portal
                    </Button>

                    {scannedFiles.length > 0 && (
                        <Button
                            onClick={handleClearAll}
                            color="error"
                            variant="text"
                            sx={{
                                textTransform: 'none',
                                fontWeight: '700',
                                borderRadius: '8px',
                                px: 2,
                                '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                            }}
                        >
                            Clear Unsaved Queue ({scannedFiles.length})
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* LIGHTBOX OVERLAY */}
            <Backdrop
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1000, color: '#fff', bgcolor: 'rgba(10, 15, 30, 0.94)', display: 'flex', flexDirection: 'column', overflow: 'auto', p: 1 }}
                open={Boolean(zoomImageSrc)}
            >
                <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1, zIndex: 10 }}>
                    <IconButton onClick={() => setIsImageZoomedIn(!isImageZoomedIn)} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                        {isImageZoomedIn ? <ZoomOutIcon /> : <ZoomInIcon />}
                    </IconButton>
                    <IconButton onClick={() => { setZoomImageSrc(null); setIsImageZoomedIn(false); }} sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    onClick={() => zoomImageSrc && setIsImageZoomedIn(!isImageZoomedIn)}
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: isImageZoomedIn ? 'flex-start' : 'center',
                        cursor: isImageZoomedIn ? 'zoom-out' : 'zoom-in',
                        p: { xs: 1, sm: 4 }
                    }}
                >
                    {zoomImageSrc && (
                        <Box
                            component="img"
                            src={zoomImageSrc}
                            alt="Zoomed Receipt"
                            sx={{
                                maxWidth: isImageZoomedIn ? 'none' : '95vw',
                                maxHeight: isImageZoomedIn ? 'none' : '85vh',
                                transform: isImageZoomedIn ? { xs: 'scale(1.8)', sm: 'scale(2.5)' } : 'scale(1)',
                                transformOrigin: 'top center',
                                transition: 'transform 0.2s ease-in-out',
                                borderRadius: '4px',
                                my: isImageZoomedIn ? 8 : 0
                            }}
                        />
                    )}
                </Box>
            </Backdrop>
        </>
    );
}